import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import { CcLibraryItem, CcLibraryHeading } from "server/models"
import { ADD, EDIT, IDLE, PENDING } from "const"
import { StateType, hasAccess, hasProjectAccess } from "client/utils"
import {
    PRM_CC_LIBRARY,
    StoreType,
    CcLibraryItemProps,

    LoadCcLibraryItemById,
    LoadCcLibrarySetById,
    LoadNestedItems,
    IsOwnerCcLibrarySet,
    IsOwnerCcLibraryItem,

    setCcLibraryItemEdit,
    setCcLibrarySetView,
    setCcLibraryItemReloadEdit,
    setCcLibraryItemList,
    setCcLibraryHeadingList,
    setCcLibraryItemNested,
    setCcLibraryHeadingAvailableParents,
    setCcLibraryItemAvailableParents,
    initCcLibraryItem,
} from "client/store"
import {
    Container,
    PageHeader,
    PageTitle,
    PageContent,
    Loader,
    TableGroupedIds,
    TableNestedIds,
} from "client/components"

import NotFound from "client/parts/notFound"
import AccessDenied from "client/parts/accessDenied"
import Form from "./form"

const EditItem = ({
    setId,
    itemId,
}: CcLibraryItemProps) => {
    const set = useSelector((state: StoreType) => state.ccLibrarySet.setView)
    const nested = useSelector((state: StoreType) => state.ccLibraryItem.itemGroupedIds)
    const itemList = useSelector((state: StoreType) => state.ccLibraryItem.itemList)
    const headingList = useSelector((state: StoreType) => state.ccLibraryHeading.headingList)

    const item = useSelector((state: StoreType) => state.ccLibraryItem.itemEdit)
    const reloadEdit = useSelector((state: StoreType) => state.ccLibraryItem.reloadEdit)

    const project = useSelector((state: StoreType) => state.project.projectView)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [loadState, setLoadState] = useState<StateType>(IDLE)

    const reload = () => {
        dispatch(setCcLibraryItemReloadEdit(false))
        setTimeout(() => {
            dispatch(setCcLibraryItemReloadEdit(true))
        }, 0)
    }

    useEffect(() => {
        setLoadState(PENDING)
        const promiseArray: Promise<any>[] = []
        if (!nested) {
            promiseArray.push(
                LoadNestedItems(setId, project.id, userData.accountId)
                .then((res) => {
                    dispatch(setCcLibraryItemList(res.itemList))
                    dispatch(setCcLibraryHeadingList(res.headingList))
                    dispatch(setCcLibraryItemNested(res.nested))
                }),
            )
        }
        if (!set || set.id !== setId) {
            promiseArray.push(
                LoadCcLibrarySetById(setId, userData.accountId, project.id).then((res) => {
                    dispatch(setCcLibrarySetView(res))
                }),
            )
        }

        if (itemId === 0) {
            dispatch(setCcLibraryItemEdit(initCcLibraryItem))
        } else {
            promiseArray.push(
                LoadCcLibraryItemById(itemId, setId).then((res) => {
                    dispatch(setCcLibraryItemEdit(res))
                }),
            )
        }

        Promise.all(promiseArray).then(() => {
            setLoadState(IDLE)
            reload()
        })
    }, [])

    useEffect(() => {
        if (!nested) return

        const availableItems: CcLibraryItem[] = []
        const availableHeadings: CcLibraryHeading[] = []

        const addItems = (nestedItems: readonly TableNestedIds[]) => {
            nestedItems.forEach((row) => {
                if (row.id !== item.id) {
                    availableItems.push(itemList[Number(row.id)])
                    addItems(row.nestedIds)
                }
            })
        }

        const addHeadings = (nestedGroupes: readonly TableGroupedIds[]) => {
            nestedGroupes.forEach((row) => {
                availableHeadings.push(headingList[row.groupId])
                addItems(row.nestedIds)
                addHeadings(row.nestedGroupedIds)
            })
        }
        addItems(nested.nestedIds)
        addHeadings(nested.nestedGroupedIds)

        dispatch(setCcLibraryHeadingAvailableParents(availableHeadings))
        dispatch(setCcLibraryItemAvailableParents(availableItems))
    }, [nested, headingList, itemList, item])

    const addAccess = !project.id
        ? hasAccess(PRM_CC_LIBRARY, ADD)
        : hasProjectAccess(PRM_CC_LIBRARY, ADD)

    const editAccess = !project.id
        ? hasAccess(PRM_CC_LIBRARY, EDIT)
        : hasProjectAccess(PRM_CC_LIBRARY, EDIT)

    const isSetOwner = IsOwnerCcLibrarySet(project.id, set, userData)
    const isItemOwner = IsOwnerCcLibraryItem(project.id, item, userData)

    const allowedToEdit = editAccess || isSetOwner || isItemOwner

    if (itemId === 0 && !addAccess) return <AccessDenied />
    if (itemId !== 0 && !allowedToEdit) return <AccessDenied />
    if (!set || !item) return <NotFound />

    return (
        <Container>
            <PageHeader>
                <PageTitle>
                    {item.id ? `${lang.editCompliantItem} # ${item.id}` : lang.addCompliantItem}
                </PageTitle>
            </PageHeader>
            <PageContent>
                {loadState === PENDING && <Loader />}
                {
                    !reloadEdit
                    && <Form item={item} />
                }
                {
                    reloadEdit
                    && <Form item={item} />
                }
            </PageContent>
        </Container>
    )
}
export default EditItem
